import React from 'react';
import { useState, useCallback } from 'react';
import { Rocket, ArrowRight } from 'lucide-react';
import ContactForm from './ContactForm';

const FinalCTA = () => {
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);

  const handleGetStarted = useCallback(() => {
    if (typeof window !== 'undefined' && (window as any).voiceflow?.chat?.open) {
      try {
        (window as any).voiceflow.chat.open();
        return;
      } catch (error) {
        console.error('Error opening Voiceflow:', error);
      }
    }
    // Fallback to contact form if widget hasn't loaded
    setIsContactFormOpen(true);
  }, []);

  const handleCloseContactForm = useCallback(() => {
    setIsContactFormOpen(false);
  }, []);

  return (
    <section className="py-20 px-6 bg-black tech-grid relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-pulse" />
      <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      <div className="absolute bottom-1/4 left-1/4 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }} />
      
      <div className="max-w-4xl mx-auto text-center relative z-10">
        {/* Icon */}
        <div className="w-20 h-20 mx-auto mb-8 bg-gradient-to-r from-purple-600/20 to-gray-400/20 rounded-2xl flex items-center justify-center">
          <Rocket className="w-10 h-10 text-gray-300" />
        </div>

        {/* Title */}
        <h2 className="text-4xl md:text-6xl font-display text-white mb-8 tracking-wide leading-tight">
          Not sure what to{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-emerald-400 to-purple-400 glow-text">
            automate
          </span>
          <br />
          first?
        </h2>

        {/* Description */}
        <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto leading-relaxed font-tech">
          Most people start with the one that's costing them work — usually the enquiries that arrive when nobody's free to answer. Tell me what your week actually looks like and I'll tell you where the easy win is.
        </p>

        {/* Honest Points */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          <div className="p-6 rounded-xl bg-gray-900/30 border border-gray-800/50 backdrop-blur-sm">
            <div className="text-2xl font-bold text-blue-400 mb-2 font-robotic">Small business specialists</div>
            <div className="text-gray-400 text-sm font-tech">We build for owners, not boardrooms</div>
          </div>
          <div className="p-6 rounded-xl bg-gray-900/30 border border-gray-800/50 backdrop-blur-sm">
            <div className="text-2xl font-bold text-emerald-400 mb-2 font-robotic">See our work</div>
            <div className="text-gray-400 text-sm font-tech">A live product you can try before you talk to us</div>
          </div>
          <div className="p-6 rounded-xl bg-gray-900/30 border border-gray-800/50 backdrop-blur-sm">
            <div className="text-2xl font-bold text-red-400 mb-2 font-robotic">Month to month</div>
            <div className="text-gray-400 text-sm font-tech">No contracts. Cancel whenever you like.</div>
          </div>
        </div>

        {/* CTA Button */}
        <button 
          onClick={handleGetStarted}
          type="button"
          style={{ cursor: 'pointer' }}
          className="group relative px-12 py-5 animated-border rounded-xl text-white font-bold text-xl tracking-wide hover:shadow-2xl hover:shadow-purple-600/30 transition-all duration-300 hover:scale-105 active:scale-95 glow-silver"
        >
          <span className="relative z-10 font-robotic flex items-center">
            Talk to Our AI Assistant
            <ArrowRight className="w-6 h-6 ml-3 group-hover:translate-x-1 transition-transform duration-300" />
          </span>
          <div className="absolute inset-1 bg-gradient-to-r from-purple-700 via-blue-600 to-emerald-500 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </button>

        {/* Trust Indicators */}
        <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-6 text-sm text-gray-500 font-tech">
          <div className="flex items-center">
            <div className="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
            No setup fees on monthly plans
          </div>
          <div className="flex items-center">
            <div className="w-2 h-2 bg-emerald-400 rounded-full mr-2"></div>
            Cancel anytime
          </div>
        </div>
      </div>

      {/* Contact Form Modal */}
      <ContactForm 
        isOpen={isContactFormOpen} 
        onClose={handleCloseContactForm} 
      />
    </section>
  );
};

export default FinalCTA;